import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';

import FilterEnum from 'pages/verification/admin/filters/data';

type SortOrder = 'asc' | 'desc';

export type VerificationListFilters = Record<string, unknown>;

export const DEFAULT_SORT_BY = 'bcqpBurnCenterName';

export const createInitialVerificationListFilters = (): VerificationListFilters => (
  Object.values(FilterEnum).reduce<VerificationListFilters>(
    (res, key) => ({ ...res, [key]: null }),
    {},
  )
);

interface VerificationListFilterState {
  page: number;
  sortBy: string;
  sortOrder: SortOrder;
  filters: VerificationListFilters;
  showExtraFilters: boolean;
}

interface VerificationListFilterContextValue extends VerificationListFilterState {
  handleFilterChange: (name: string, value: unknown) => void;
  handleFiltersReplace: (filters: VerificationListFilters) => void;
  handlePageChange: (page: number) => void;
  handleSortChange: (sortField: string) => void;
  toggleExtraFilters: () => void;
}

const createInitialState = (): VerificationListFilterState => ({
  page: 0,
  sortBy: DEFAULT_SORT_BY,
  sortOrder: 'asc',
  filters: createInitialVerificationListFilters(),
  showExtraFilters: false,
});

const VerificationListFilterContext = React.createContext<
  VerificationListFilterContextValue | undefined
>(undefined);

interface VerificationListFilterProviderProps {
  children: ReactNode;
}

export const VerificationListFilterProvider: React.FC<
  VerificationListFilterProviderProps
> = ({ children }) => {
  const [state, setState] = useState<VerificationListFilterState>(createInitialState);

  const handleFilterChange = useCallback((name: string, value: unknown) => {
    setState((prev) => ({
      ...prev,
      page: 0,
      filters: { ...prev.filters, [name]: value },
    }));
  }, []);

  const handleFiltersReplace = useCallback((filters: VerificationListFilters) => {
    setState((prev) => ({
      ...prev,
      page: 0,
      filters,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setState((prev) => ({ ...prev, page }));
  }, []);

  const handleSortChange = useCallback((sortField: string) => {
    setState((prev) => {
      if (sortField === prev.sortBy) {
        return {
          ...prev,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        ...prev,
        sortBy: sortField,
        sortOrder: 'asc',
      };
    });
  }, []);

  const toggleExtraFilters = useCallback(() => {
    setState((prev) => {
      if (prev.showExtraFilters) {
        return {
          ...prev,
          showExtraFilters: false,
          page: 0,
          filters: {
            ...createInitialVerificationListFilters(),
            [FilterEnum.burncenter]: prev.filters[FilterEnum.burncenter],
          },
        };
      }

      return {
        ...prev,
        showExtraFilters: true,
      };
    });
  }, []);

  const value = useMemo<VerificationListFilterContextValue>(
    () => ({
      ...state,
      handleFilterChange,
      handleFiltersReplace,
      handlePageChange,
      handleSortChange,
      toggleExtraFilters,
    }),
    [
      state,
      handleFilterChange,
      handleFiltersReplace,
      handlePageChange,
      handleSortChange,
      toggleExtraFilters,
    ],
  );

  return (
    <VerificationListFilterContext.Provider value={value}>
      {children}
    </VerificationListFilterContext.Provider>
  );
};

export const useVerificationListFilter = (): VerificationListFilterContextValue => {
  const context = useContext(VerificationListFilterContext);

  if (!context) {
    throw new Error(
      'useVerificationListFilter must be used within VerificationListFilterProvider',
    );
  }

  return context;
};
